// @boty/database - Supabase Client
// Provides typed Supabase clients for browser and server contexts

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

/**
 * Create a Supabase client for browser usage.
 * Uses the public anon key which is safe to expose in client-side code.
 * RLS policies will be enforced based on the authenticated user.
 *
 * @returns Typed Supabase client for browser context
 */
export function createBrowserClient(): SupabaseClient<Database> {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Missing Supabase environment variables. ' +
        'Ensure NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY are set.'
    );
  }

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
}

/**
 * Create a Supabase client for server-side usage.
 * Uses the service role key which bypasses RLS.
 * NEVER expose the service role key to the browser.
 *
 * @returns Typed Supabase client for server context with admin privileges
 */
export function createServerClient(): SupabaseClient<Database> {
  const supabaseUrl =
    process.env.SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseServiceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;

  if (!supabaseUrl || !supabaseServiceKey) {
    throw new Error(
      'Missing Supabase server environment variables. ' +
        'Ensure SUPABASE_URL (or NEXT_PUBLIC_SUPABASE_URL) and SUPABASE_SERVICE_ROLE_KEY are set.'
    );
  }

  return createClient<Database>(supabaseUrl, supabaseServiceKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
  });
}

/**
 * Create a Supabase client for anonymous/unauthenticated access.
 * Used for temporary bot operations where session_id is passed explicitly.
 *
 * NOTE: Session-based RLS policies (using current_setting('app.session_id'))
 * require server-side handling via Edge Functions or API routes that can
 * set PostgreSQL session variables. For client-side temporary bot access,
 * use the query functions that explicitly filter by session_id (e.g.,
 * getSessionBots, sessionOwnBot).
 *
 * @returns Typed Supabase client for anonymous context
 */
export function createAnonClient(): SupabaseClient<Database> {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Missing Supabase environment variables. ' +
        'Ensure NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY are set.'
    );
  }

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
  });
}

/**
 * @deprecated Use createAnonClient() instead. Session-based RLS requires
 * server-side PostgreSQL session variable setting which HTTP headers cannot provide.
 * For temporary bot access, use query functions that filter by session_id explicitly.
 */
export const createSessionClient = createAnonClient;

// Re-export types for convenience
export type { SupabaseClient } from '@supabase/supabase-js';
export type { Database };
